import { useContext } from 'react';
import { ControlSelect } from '../../components/ControlSelect/ControlSelect';
import { QuizQuestionScreen } from '../../components/QuizQuestionScreen/QuizQuestionScreen';
import { SectionLabel } from '../../components/SectionLabel/SectionLabel';
import { Toggle, ToggleVariant } from '../../components/Toggle/Toggle/Toggle';
import { Goals } from '../../defs/quiz';
import { QuizContext } from '../../quiz-context';
import { shuffleArray } from '../../utils/shuffleArray';
import styles from '../layout.module.css';

// If the user has persisted goals from a previous session when a different set of possible goals was rendered, remove any goals that are no longer valid. When the experiment code is removed. This logic will need to be updated to handle users who might have persisted data from the losing variant.
const getValidPersistedGoals = (
  persistedGoals: number[] | null,
  validGoals: Set<Goals>
): Goals[] => persistedGoals?.filter((goal) => validGoals.has(goal)) ?? [];

const goalsAnswers = [
  {
    label: 'Save time',
    value: Goals.SaveTime,
  },
  {
    label: 'Improve health',
    value: Goals.ImproveHealth,
  },
  {
    label: 'Lose weight',
    value: Goals.LoseWeight,
  },
  {
    label: 'Grocery shop less',
    value: Goals.GroceryShopLess,
  },
  {
    label: 'Waste less food',
    value: Goals.WasteLessFood,
  },
  {
    label: 'Simplify cooking',
    value: Goals.SimplifyCooking,
  },
  {
    label: 'Save money',
    value: Goals.SaveMoney,
  },
  {
    label: 'Lower stress',
    value: Goals.LowerStress,
  },
];

const shuffledGoalsOptions = shuffleArray(goalsAnswers);

interface Props {
  testid: string;
}

export function HelloGoals(props: Props) {
  const { testid } = props;

  const { quiz } = useContext(QuizContext);

  const goals = getValidPersistedGoals(
    quiz.goals,
    new Set(shuffledGoalsOptions.map((option) => option.value))
  );

  return (
    <QuizQuestionScreen
      testid={testid}
      heroText="What are your goals with Hungryroot?"
      subCopy="Select 3 that are most important to you."
      sectionLabel={<SectionLabel label="About you" />}
    >
      <div
        data-testid={`${testid}-question-goals`}
        className={styles['bottom-padding']}
      >
        <ControlSelect
          question="goals"
          max={3}
          defaultValue={[]}
          layoutVariant="two-column-desktop"
          ariaLabel="Select your goals"
          items={shuffledGoalsOptions}
          selectedKeys={goals}
        >
          {shuffledGoalsOptions &&
            goals &&
            shuffledGoalsOptions.map(({ label, value }, i) => {
              const isActive = goals.includes(value);
              return (
                <ControlSelect.Option id={value} key={value} label={label}>
                  <Toggle
                    active={isActive}
                    testid={`${testid}-toggle-goal-${value}`}
                    label={label}
                    variant={ToggleVariant.Two}
                    style={{ animationDelay: `${0.05 * i + 0.5}s` }}
                  />
                </ControlSelect.Option>
              );
            })}
        </ControlSelect>
      </div>
    </QuizQuestionScreen>
  );
}
